import { RadioGroup, RadioGroupItem } from "@/modules/shared/ui/components/radio-group"
import { Label } from "@/modules/shared/ui/components/label"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { cn } from "@/lib/utils"
import { ScorecardCriterion } from "@/modules/recruitment/entities/ScorecardCriterion"
import { SCORE_LABELS, SCORE_STYLES } from "@/modules/recruitment/applications/ui/components/scorecard-styles"

interface ScorecardCriterionInputProps {
  criterion: ScorecardCriterion
  weightShare: number
  score: number | null
  evidence: string
  onScoreChange: (score: number) => void
  onEvidenceChange: (evidence: string) => void
  disabled?: boolean
}

export function ScorecardCriterionInput({
  criterion,
  weightShare,
  score,
  evidence,
  onScoreChange,
  onEvidenceChange,
  disabled,
}: ScorecardCriterionInputProps) {
  return (
    <div className="py-5 border-b border-border last:border-b-0 space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <span className="font-medium text-sm">{criterion.label}</span>
        <span className="text-xs px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-900/50">
          weight {weightShare}%
        </span>
      </div>

      {criterion.description && (
        <p className="text-sm text-muted-foreground">{criterion.description}</p>
      )}

      <RadioGroup
        value={score ? String(score) : undefined}
        onValueChange={(value) => onScoreChange(Number(value))}
        disabled={disabled}
        className="flex flex-row items-center gap-2 flex-wrap"
      >
        {[1, 2, 3, 4].map((value) => {
          const isSelected = score === value
          return (
            <div key={value}>
              <RadioGroupItem
                value={String(value)}
                id={`${criterion.id}-${value}`}
                className="peer sr-only"
              />
              <Label
                htmlFor={`${criterion.id}-${value}`}
                className={cn(
                  "flex h-9 w-11 items-center justify-center rounded-md border text-sm font-medium transition-colors",
                  "peer-focus-visible:ring-[3px] peer-focus-visible:ring-ring/50",
                  disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                  isSelected
                    ? SCORE_STYLES[value]
                    : "border-border text-muted-foreground hover:bg-muted",
                )}
              >
                {value}
              </Label>
            </div>
          )
        })}
        <span className="text-xs text-muted-foreground ml-1">
          {score ? SCORE_LABELS[score] : "Not yet rated"}
        </span>
      </RadioGroup>

      <Textarea
        value={evidence}
        onChange={(e) => onEvidenceChange(e.target.value)}
        placeholder="What did you observe that supports this score?"
        disabled={disabled}
        className="min-h-16"
      />
    </div>
  )
}
